<template>
  <div role="main" class="p-6 pt-0">
    <HLSpace vertical :size="24">
      <!-- Page Header -->
      <PageHeader
        :title="'Assigned to Me'"
        :icon-path="ICONS.user"
        :helper-text-mobile="'View items assigned to you'"
        :helper-text-desktop="'View all features, bugs, test runs, and accessibility issues assigned to you'"
      />

      <!-- Filter Tabs -->
      <div class="bg-white rounded-lg shadow p-4">
        <HLTabs v-model:value="activeFilter">
          <HLTab name="features">
            Features ({{ filteredFeatures.length }})
          </HLTab>
          <HLTab name="test-runs">
            Test Runs ({{ filteredTestRuns.length }})
          </HLTab>
          <HLTab name="bugs">
            Bugs ({{ filteredBugs.length }})
          </HLTab>
          <HLTab name="accessibility">
            Accessibility Issues ({{ filteredAccessibilityIssues.length }})
          </HLTab>
        </HLTabs>
      </div>

      <!-- Features Tab -->
      <div v-if="activeFilter === 'features'">
        <SectionFilter
          title="Assigned Features"
          :show-search="false"
          :active-filter-count="activeFeatureFiltersCount"
          :has-active-filters="activeFeatureFiltersCount > 0"
          :show-export="false"
          :show-filters="true"
          :expanded="showFeatureFilters"
          :filtered-count="filteredFeatures.length"
          :total-count="assignedFeatures.length"
          @toggle="showFeatureFilters = !showFeatureFilters"
          @clear="clearFeatureFilters"
        >
          <template #default>
            <!-- Search Bar -->
            <div class="col-span-full mb-4">
              <HLInput
                v-model="featureFilters.search"
                placeholder="Search features by title, description, or ID..."
                clearable
                class="w-full"
              >
                <template #prefix>
                  <Icon name="search" size="sm" class="text-gray-400" />
                </template>
              </HLInput>
            </div>

            <!-- Filters -->
            <div class="col-span-full">
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Project Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Project</HLText>
                  <HLSelect
                    v-model:value="featureFilters.project"
                    :options="projectSelectOptions"
                    placeholder="All Projects"
                    multiple
                    filterable
                  />
                </div>

                <!-- Status Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Status</HLText>
                  <HLSelect
                    v-model:value="featureFilters.status"
                    :options="statusSelectOptions"
                    placeholder="All Status"
                    multiple
                    filterable
                  />
                </div>

                <!-- Priority Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Priority</HLText>
                  <HLSelect
                    v-model:value="featureFilters.priority"
                    :options="prioritySelectOptions"
                    placeholder="All Priorities"
                    multiple
                    filterable
                  />
                </div>

                <!-- Assignment Type Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Assignment Type</HLText>
                  <HLSelect
                    v-model:value="featureFilters.assignmentType"
                    :options="assignmentTypeOptions"
                    placeholder="All Types"
                    multiple
                    filterable
                  />
                </div>
              </div>
            </div>
          </template>
        </SectionFilter>

        <!-- Features Table -->
        <div v-if="loadingFeatures" class="text-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p class="mt-2 text-gray-500">Loading features...</p>
        </div>
        <div v-else-if="filteredFeatures.length === 0" class="text-center py-12 bg-white rounded-lg shadow">
          <p class="text-gray-500">No features assigned to you.</p>
        </div>
        <div v-else class="bg-white rounded-lg shadow overflow-hidden">
          <HLDataTableWrapper responsive-column-width max-width="100%" max-height="400px">
            <HLDataTable
              :columns="featureColumns"
              :data="filteredFeatures"
              :loading="loadingFeatures"
              row-key="id"
              @row-click="(row) => goToFeature(row.original)"
            />
          </HLDataTableWrapper>
        </div>
      </div>

      <!-- Test Runs Tab -->
      <div v-if="activeFilter === 'test-runs'">
        <SectionFilter
          title="Assigned Test Runs"
          :show-search="false"
          :active-filter-count="activeTestRunFiltersCount"
          :has-active-filters="activeTestRunFiltersCount > 0"
          :show-export="false"
          :show-filters="true"
          :expanded="showTestRunFilters"
          :filtered-count="filteredTestRuns.length"
          :total-count="assignedTestRuns.length"
          @toggle="showTestRunFilters = !showTestRunFilters"
          @clear="clearTestRunFilters"
        >
          <template #default>
            <!-- Search Bar -->
            <div class="col-span-full mb-4">
              <HLInput
                v-model="testRunFilters.search"
                placeholder="Search test runs by name..."
                clearable
                class="w-full"
              >
                <template #prefix>
                  <Icon name="search" size="sm" class="text-gray-400" />
                </template>
              </HLInput>
            </div>

            <!-- Filters -->
            <div class="col-span-full">
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <!-- Project Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Project</HLText>
                  <HLSelect
                    v-model:value="testRunFilters.project"
                    :options="projectSelectOptions"
                    placeholder="All Projects"
                    multiple
                    filterable
                  />
                </div>

                <!-- Status Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Status</HLText>
                  <HLSelect
                    v-model:value="testRunFilters.status"
                    :options="testRunStatusSelectOptions"
                    placeholder="All Status"
                    multiple
                    filterable
                  />
                </div>
              </div>
            </div>
          </template>
        </SectionFilter>

        <!-- Test Runs Table -->
        <div v-if="loadingTestRuns" class="text-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p class="mt-2 text-gray-500">Loading test runs...</p>
        </div>
        <div v-else-if="filteredTestRuns.length === 0" class="text-center py-12 bg-white rounded-lg shadow">
          <p class="text-gray-500">No test runs with test cases assigned to you.</p>
        </div>
        <div v-else class="bg-white rounded-lg shadow overflow-hidden">
          <HLDataTableWrapper responsive-column-width max-width="100%" max-height="400px">
            <HLDataTable
              :columns="testRunColumns"
              :data="filteredTestRuns"
              :loading="loadingTestRuns"
              row-key="id"
              @row-click="(row) => goToTestRun(row.original)"
            />
          </HLDataTableWrapper>
        </div>
      </div>

      <!-- Bugs Tab -->
      <div v-if="activeFilter === 'bugs'">
        <SectionFilter
          title="Assigned Bugs"
          :show-search="false"
          :active-filter-count="activeBugFiltersCount"
          :has-active-filters="activeBugFiltersCount > 0"
          :show-export="false"
          :show-filters="true"
          :expanded="showBugFilters"
          :filtered-count="filteredBugs.length"
          :total-count="assignedBugs.length"
          @toggle="showBugFilters = !showBugFilters"
          @clear="clearBugFilters"
        >
          <template #default>
            <!-- Search Bar -->
            <div class="col-span-full mb-4">
              <HLInput
                v-model="bugFilters.search"
                placeholder="Search bugs by title, description, or ID..."
                clearable
                class="w-full"
              >
                <template #prefix>
                  <Icon name="search" size="sm" class="text-gray-400" />
                </template>
              </HLInput>
            </div>

            <!-- Filters -->
            <div class="col-span-full">
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Project Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Project</HLText>
                  <HLSelect
                    v-model:value="bugFilters.project"
                    :options="projectSelectOptions"
                    placeholder="All Projects"
                    multiple
                    filterable
                  />
                </div>

                <!-- Status Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Status</HLText>
                  <HLSelect
                    v-model:value="bugFilters.status"
                    :options="bugStatusSelectOptions"
                    placeholder="All Status"
                    multiple
                    filterable
                  />
                </div>

                <!-- Priority Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Priority</HLText>
                  <HLSelect
                    v-model:value="bugFilters.priority"
                    :options="bugPrioritySelectOptions"
                    placeholder="All Priorities"
                    multiple
                    filterable
                  />
                </div>

                <!-- Severity Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Severity</HLText>
                  <HLSelect
                    v-model:value="bugFilters.severity"
                    :options="bugSeveritySelectOptions"
                    placeholder="All Severities"
                    multiple
                    filterable
                  />
                </div>
              </div>
            </div>
          </template>
        </SectionFilter>

        <!-- Bugs Table -->
        <div v-if="loadingBugs" class="text-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p class="mt-2 text-gray-500">Loading bugs...</p>
        </div>
        <div v-else-if="filteredBugs.length === 0" class="text-center py-12 bg-white rounded-lg shadow">
          <p class="text-gray-500">No bugs assigned to you.</p>
        </div>
        <div v-else class="bg-white rounded-lg shadow overflow-hidden">
          <HLDataTableWrapper responsive-column-width max-width="100%" max-height="400px">
            <HLDataTable
              :columns="bugColumns"
              :data="filteredBugs"
              :loading="loadingBugs"
              row-key="id"
              @row-click="(row) => goToBug(row.original)"
            />
          </HLDataTableWrapper>
        </div>
      </div>

      <!-- Accessibility Issues Tab -->
      <div v-if="activeFilter === 'accessibility'">
        <SectionFilter
          title="Assigned Accessibility Issues"
          :show-search="false"
          :active-filter-count="activeAccessibilityFiltersCount"
          :has-active-filters="activeAccessibilityFiltersCount > 0"
          :show-export="false"
          :show-filters="true"
          :expanded="showAccessibilityFilters"
          :filtered-count="filteredAccessibilityIssues.length"
          :total-count="assignedAccessibilityIssues.length"
          @toggle="showAccessibilityFilters = !showAccessibilityFilters"
          @clear="clearAccessibilityFilters"
        >
          <template #default>
            <!-- Search Bar -->
            <div class="col-span-full mb-4">
              <HLInput
                v-model="accessibilityFilters.search"
                placeholder="Search accessibility issues by description, page URL, or issue ID..."
                clearable
                class="w-full"
              >
                <template #prefix>
                  <Icon name="search" size="sm" class="text-gray-400" />
                </template>
              </HLInput>
            </div>

            <!-- Filters -->
            <div class="col-span-full">
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <!-- Status Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Status</HLText>
                  <HLSelect
                    v-model:value="accessibilityFilters.status"
                    :options="accessibilityStatusSelectOptions"
                    placeholder="All Status"
                    multiple
                    filterable
                  />
                </div>

                <!-- Impact Filter -->
                <div>
                  <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Impact</HLText>
                  <HLSelect
                    v-model:value="accessibilityFilters.impact"
                    :options="accessibilityImpactSelectOptions"
                    placeholder="All Impacts"
                    multiple
                    filterable
                  />
                </div>
              </div>
            </div>
          </template>
        </SectionFilter>

        <!-- Accessibility Issues Table -->
        <div v-if="loadingAccessibilityIssues" class="text-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p class="mt-2 text-gray-500">Loading accessibility issues...</p>
        </div>
        <div v-else-if="filteredAccessibilityIssues.length === 0" class="text-center py-12 bg-white rounded-lg shadow">
          <p class="text-gray-500">No accessibility issues assigned to you.</p>
        </div>
        <div v-else class="bg-white rounded-lg shadow overflow-hidden">
          <HLDataTableWrapper responsive-column-width max-width="100%" max-height="400px">
            <HLDataTable
              :columns="accessibilityColumns"
              :data="filteredAccessibilityIssues"
              :loading="loadingAccessibilityIssues"
              row-key="id"
              @row-click="(row) => goToAccessibilityIssue(row.original)"
            />
          </HLDataTableWrapper>
        </div>
      </div>
    </HLSpace>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, h } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import PageHeader from '@/components/ui/PageHeader.vue'
import SectionFilter from '@/components/reports/SectionFilter.vue'
import Icon from '@/components/ui/Icon.vue'
import { HLButton, HLSpace, HLSelect, HLInput, HLText, HLDataTable, HLDataTableWrapper, HLPagination, HLTag, HLTabs, HLTab } from '@platform-ui/highrise'
import { featureService, bugService, testRunService, projectService, accessibilityService } from '@/database/services'
import { ICONS } from '@/constants/icons'
import { formatDate } from '@/utils/dateUtils'
import { FEATURE_STATUS_OPTIONS, FEATURE_PRIORITY_OPTIONS } from '@/constants/featureConstants'
import { BUG_STATUS_OPTIONS, BUG_PRIORITY_OPTIONS, BUG_SEVERITY_OPTIONS } from '@/constants/bugs'
import { TEST_RUN_STATUS_OPTIONS } from '@/constants/testRunConstants'
import { ACCESSIBILITY_STATUS_OPTIONS, ACCESSIBILITY_IMPACT, ACCESSIBILITY_STATUS_COLORS, ACCESSIBILITY_IMPACT_COLORS, ACCESSIBILITY_STATUS_LABELS, ACCESSIBILITY_IMPACT_LABELS } from '@/constants/accessibility'

const router = useRouter()
const authStore = useAuthStore()

const activeFilter = ref('features')
const loadingFeatures = ref(false)
const loadingBugs = ref(false)
const loadingTestRuns = ref(false)
const loadingProjects = ref(false)
const loadingAccessibilityIssues = ref(false)

const features = ref([])
const bugs = ref([])
const testRuns = ref([])
const projects = ref([])
const accessibilityIssues = ref([])

const showFeatureFilters = ref(false)
const showBugFilters = ref(false)
const showTestRunFilters = ref(false)
const showAccessibilityFilters = ref(false)

const currentUserId = computed(() => authStore.user?.id || authStore.user?.uid)

// Feature Filters
const featureFilters = ref({
  search: '',
  project: [],
  status: [],
  priority: [],
  assignmentType: []
})

// Bug Filters
const bugFilters = ref({
  search: '',
  project: [],
  status: [],
  priority: [],
  severity: []
})

// Test Run Filters
const testRunFilters = ref({
  search: '',
  project: [],
  status: []
})

// Accessibility Issues Filters
const accessibilityFilters = ref({
  search: '',
  status: [],
  impact: []
})

// Filter assigned items
const assignedFeatures = computed(() => {
  if (!currentUserId.value) return []
  return features.value.filter(feature => 
    (feature.dev_assignee === currentUserId.value || 
     feature.qa_assignee === currentUserId.value) &&
    !feature.is_deleted &&
    !feature.isDeleted
  )
})

const assignedBugs = computed(() => {
  if (!currentUserId.value) return []
  return bugs.value.filter(bug => 
    bug.assigned_to === currentUserId.value && 
    !bug.is_deleted && 
    !bug.isDeleted
  )
})

const assignedTestRuns = computed(() => {
  if (!currentUserId.value) return []
  return testRuns.value.filter(testRun => {
    // Check if test run has any test cases assigned to current user
    const testCasesInRun = testRun.test_cases || []
    const hasAssignedTestCase = testCasesInRun.some(tc => 
      (tc.assigned_to === currentUserId.value || tc.assigneeId === currentUserId.value)
    )
    return hasAssignedTestCase && !testRun.is_deleted && !testRun.isDeleted
  })
})

const assignedAccessibilityIssues = computed(() => {
  if (!currentUserId.value) return []
  return accessibilityIssues.value.filter(issue => 
    issue.assigned_to === currentUserId.value && 
    !issue.is_deleted
  )
})

// Filtered items
const filteredFeatures = computed(() => {
  let result = assignedFeatures.value

  // Search filter
  if (featureFilters.value.search) {
    const searchLower = featureFilters.value.search.toLowerCase()
    result = result.filter(f => 
      f.title?.toLowerCase().includes(searchLower) ||
      f.featureId?.toLowerCase().includes(searchLower) ||
      f.description?.toLowerCase().includes(searchLower)
    )
  }

  // Project filter
  if (featureFilters.value.project.length > 0) {
    result = result.filter(f => featureFilters.value.project.includes(f.project_id))
  }

  // Status filter
  if (featureFilters.value.status.length > 0) {
    result = result.filter(f => featureFilters.value.status.includes(f.status))
  }

  // Priority filter
  if (featureFilters.value.priority.length > 0) {
    result = result.filter(f => featureFilters.value.priority.includes(f.priority))
  }

  // Assignment type filter
  if (featureFilters.value.assignmentType.length > 0) {
    result = result.filter(f => {
      if (featureFilters.value.assignmentType.includes('dev') && f.dev_assignee === currentUserId.value) {
        return true
      }
      if (featureFilters.value.assignmentType.includes('qa') && f.qa_assignee === currentUserId.value) {
        return true
      }
      return false
    })
  }

  return result
})

const filteredBugs = computed(() => {
  let result = assignedBugs.value

  // Search filter
  if (bugFilters.value.search) {
    const searchLower = bugFilters.value.search.toLowerCase()
    result = result.filter(b => 
      b.title?.toLowerCase().includes(searchLower) ||
      b.bugId?.toLowerCase().includes(searchLower) ||
      b.description?.toLowerCase().includes(searchLower)
    )
  }

  // Project filter
  if (bugFilters.value.project.length > 0) {
    result = result.filter(b => bugFilters.value.project.includes(b.project_id))
  }

  // Status filter
  if (bugFilters.value.status.length > 0) {
    result = result.filter(b => bugFilters.value.status.includes(b.status))
  }

  // Priority filter
  if (bugFilters.value.priority.length > 0) {
    result = result.filter(b => bugFilters.value.priority.includes(b.priority))
  }

  // Severity filter
  if (bugFilters.value.severity.length > 0) {
    result = result.filter(b => bugFilters.value.severity.includes(b.severity))
  }

  return result
})

const filteredTestRuns = computed(() => {
  let result = assignedTestRuns.value

  // Search filter
  if (testRunFilters.value.search) {
    const searchLower = testRunFilters.value.search.toLowerCase()
    result = result.filter(tr => 
      tr.name?.toLowerCase().includes(searchLower) ||
      tr.testrun_id?.toLowerCase().includes(searchLower)
    )
  }

  // Project filter
  if (testRunFilters.value.project.length > 0) {
    result = result.filter(tr => testRunFilters.value.project.includes(tr.project_id))
  }

  // Status filter
  if (testRunFilters.value.status.length > 0) {
    result = result.filter(tr => testRunFilters.value.status.includes(tr.status))
  }

  return result
})

const filteredAccessibilityIssues = computed(() => {
  let result = assignedAccessibilityIssues.value

  // Search filter
  if (accessibilityFilters.value.search) {
    const searchLower = accessibilityFilters.value.search.toLowerCase()
    result = result.filter(issue => 
      issue.description?.toLowerCase().includes(searchLower) ||
      issue.page_url?.toLowerCase().includes(searchLower) ||
      issue.issue_id?.toLowerCase().includes(searchLower) ||
      issue.test_name?.toLowerCase().includes(searchLower)
    )
  }

  // Status filter
  if (accessibilityFilters.value.status.length > 0) {
    result = result.filter(issue => accessibilityFilters.value.status.includes(issue.status))
  }

  // Impact filter
  if (accessibilityFilters.value.impact.length > 0) {
    result = result.filter(issue => accessibilityFilters.value.impact.includes(issue.impact))
  }

  return result
})

// Filter counts
const activeFeatureFiltersCount = computed(() => {
  let count = 0
  if (featureFilters.value.search) count++
  if (featureFilters.value.project.length > 0) count++
  if (featureFilters.value.status.length > 0) count++
  if (featureFilters.value.priority.length > 0) count++
  if (featureFilters.value.assignmentType.length > 0) count++
  return count
})

const activeBugFiltersCount = computed(() => {
  let count = 0
  if (bugFilters.value.search) count++
  if (bugFilters.value.project.length > 0) count++
  if (bugFilters.value.status.length > 0) count++
  if (bugFilters.value.priority.length > 0) count++
  if (bugFilters.value.severity.length > 0) count++
  return count
})

const activeTestRunFiltersCount = computed(() => {
  let count = 0
  if (testRunFilters.value.search) count++
  if (testRunFilters.value.project.length > 0) count++
  if (testRunFilters.value.status.length > 0) count++
  return count
})

const activeAccessibilityFiltersCount = computed(() => {
  let count = 0
  if (accessibilityFilters.value.search) count++
  if (accessibilityFilters.value.status.length > 0) count++
  if (accessibilityFilters.value.impact.length > 0) count++
  return count
})

// Select options
const projectSelectOptions = computed(() => 
  projects.value.map(p => ({
    value: p.id,
    label: p.name || p.title
  }))
)

const statusSelectOptions = computed(() => 
  FEATURE_STATUS_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const prioritySelectOptions = computed(() => 
  FEATURE_PRIORITY_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const assignmentTypeOptions = computed(() => [
  { value: 'dev', label: 'Developer' },
  { value: 'qa', label: 'QA Engineer' }
])

const bugStatusSelectOptions = computed(() => 
  BUG_STATUS_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const bugPrioritySelectOptions = computed(() => 
  BUG_PRIORITY_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const bugSeveritySelectOptions = computed(() => 
  BUG_SEVERITY_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const testRunStatusSelectOptions = computed(() => 
  TEST_RUN_STATUS_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const accessibilityStatusSelectOptions = computed(() => 
  ACCESSIBILITY_STATUS_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label
  }))
)

const accessibilityImpactSelectOptions = computed(() => [
  { value: ACCESSIBILITY_IMPACT.CRITICAL, label: ACCESSIBILITY_IMPACT_LABELS.critical },
  { value: ACCESSIBILITY_IMPACT.SERIOUS, label: ACCESSIBILITY_IMPACT_LABELS.serious },
  { value: ACCESSIBILITY_IMPACT.MODERATE, label: ACCESSIBILITY_IMPACT_LABELS.moderate },
  { value: ACCESSIBILITY_IMPACT.MINOR, label: ACCESSIBILITY_IMPACT_LABELS.minor }
])

// Helper functions to get colors for status, priority, and severity
const getStatusColor = (status) => {
  const colors = {
    open: 'red',
    in_progress: 'yellow',
    resolved: 'blue',
    closed: 'green',
    reopened: 'orange',
    blocked: 'purple'
  }
  return colors[status] || 'red'
}

const getPriorityColor = (priority) => {
  const colors = {
    critical: 'red',
    high: 'orange',
    medium: 'yellow',
    low: 'gray'
  }
  return colors[priority] || 'yellow'
}

const getSeverityColor = (severity) => {
  const colors = {
    critical: 'red',
    high: 'orange',
    medium: 'yellow',
    low: 'gray'
  }
  return colors[severity] || 'yellow'
}

const getAccessibilityStatusColor = (status) => {
  return ACCESSIBILITY_STATUS_COLORS[status] || 'gray'
}

const getAccessibilityImpactColor = (impact) => {
  return ACCESSIBILITY_IMPACT_COLORS[impact] || 'gray'
}

// Helper function to map extracted color names to HLTag-compatible colors
const getHLTagColor = (colorName) => {
  const colorMap = {
    'green': 'success',
    'red': 'error',
    'blue': 'primary',
    'orange': 'warning',
    'yellow': 'warning',
    'purple': 'purple',
    'cyan': 'primary',
    'indigo': 'purple',
    'gray': 'gray'
  }
  return colorMap[colorName] || 'gray'
}

// Table columns
const featureColumns = computed(() => [
  {
    id: 'featureId',
    accessorKey: 'featureId',
    header: 'Feature ID',
    size: 150,
    cellFormatter: (props) => {
      const feature = props.row.original
      return h('div', { 
        class: 'font-medium text-blue-600 cursor-pointer hover:underline',
        onClick: (e) => {
          e.stopPropagation()
          goToFeature(feature)
        }
      }, feature.featureId || feature.id)
    }
  },
  {
    id: 'title',
    accessorKey: 'title',
    header: 'Title',
    size: 300,
    cellFormatter: (props) => {
      const feature = props.row.original
      return h('div', { class: 'font-medium' }, feature.title || 'Untitled')
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cellFormatter: (props) => {
      const feature = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          color: 'blue',
          size: 'sm',
          round: true
        }, { default: () => feature.status || 'N/A' })
      ])
    }
  },
  {
    id: 'priority',
    accessorKey: 'priority',
    header: 'Priority',
    size: 120,
    cellFormatter: (props) => {
      const feature = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          color: 'gray',
          size: 'sm',
          round: true
        }, { default: () => feature.priority || 'N/A' })
      ])
    }
  },
  {
    id: 'assignmentType',
    accessorKey: 'assignmentType',
    header: 'Assigned As',
    size: 150,
    cellFormatter: (props) => {
      const feature = props.row.original
      const types = []
      if (feature.dev_assignee === currentUserId.value) types.push('Developer')
      if (feature.qa_assignee === currentUserId.value) types.push('QA')
      return h('div', { class: 'text-sm text-gray-600' }, types.join(', ') || 'N/A')
    }
  },
  {
    id: 'project',
    accessorKey: 'project_id',
    header: 'Project',
    size: 200,
    cellFormatter: (props) => {
      const feature = props.row.original
      const project = projects.value.find(p => p.id === feature.project_id)
      return h('div', { class: 'text-sm text-gray-600' }, project?.name || project?.title || 'N/A')
    }
  }
])

const bugColumns = computed(() => [
  {
    id: 'bugId',
    accessorKey: 'bugId',
    header: 'Bug ID',
    size: 150,
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { 
        class: 'font-medium text-red-600 cursor-pointer hover:underline',
        onClick: (e) => {
          e.stopPropagation()
          goToBug(bug)
        }
      }, bug.bugId || bug.id)
    }
  },
  {
    id: 'title',
    accessorKey: 'title',
    header: 'Title',
    size: 300,
    cellFormatter: (props) => {
      const bug = props.row.original
      return h('div', { class: 'font-medium' }, bug.title || 'Untitled')
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cellFormatter: (props) => {
      const bug = props.row.original
      const statusColor = getStatusColor(bug.status)
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          color: getHLTagColor(statusColor),
          size: 'sm',
          round: true
        }, { default: () => bug.status || 'N/A' })
      ])
    }
  },
  {
    id: 'priority',
    accessorKey: 'priority',
    header: 'Priority',
    size: 120,
    cellFormatter: (props) => {
      const bug = props.row.original
      const priorityColor = getPriorityColor(bug.priority)
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          color: getHLTagColor(priorityColor),
          size: 'sm',
          round: true
        }, { default: () => bug.priority || 'N/A' })
      ])
    }
  },
  {
    id: 'severity',
    accessorKey: 'severity',
    header: 'Severity',
    size: 120,
    cellFormatter: (props) => {
      const bug = props.row.original
      const severityColor = getSeverityColor(bug.severity)
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          color: getHLTagColor(severityColor),
          size: 'sm',
          round: true
        }, { default: () => bug.severity || 'N/A' })
      ])
    }
  },
  {
    id: 'project',
    accessorKey: 'project_id',
    header: 'Project',
    size: 200,
    cellFormatter: (props) => {
      const bug = props.row.original
      const project = projects.value.find(p => p.id === bug.project_id)
      return h('div', { class: 'text-sm text-gray-600' }, project?.name || project?.title || 'N/A')
    }
  }
])

const testRunColumns = computed(() => [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    size: 250,
    cellFormatter: (props) => {
      const testRun = props.row.original
      return h('div', { 
        class: 'font-medium text-green-600 cursor-pointer hover:underline',
        onClick: (e) => {
          e.stopPropagation()
          goToTestRun(testRun)
        }
      }, testRun.name || testRun.testrun_id || testRun.id)
    }
  },
  {
    id: 'testrun_id',
    accessorKey: 'testrun_id',
    header: 'Test Run ID',
    size: 150,
    cellFormatter: (props) => {
      const testRun = props.row.original
      return h('div', { 
        class: 'text-sm text-gray-600 cursor-pointer hover:underline hover:text-green-600',
        onClick: (e) => {
          e.stopPropagation()
          goToTestRun(testRun)
        }
      }, testRun.testrun_id || 'N/A')
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cellFormatter: (props) => {
      const testRun = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          color: 'blue',
          size: 'sm',
          round: true
        }, { default: () => testRun.status || 'N/A' })
      ])
    }
  },
  {
    id: 'assignedTestCases',
    accessorKey: 'assignedTestCases',
    header: 'Assigned Test Cases',
    size: 150,
    cellFormatter: (props) => {
      const testRun = props.row.original
      const testCasesInRun = testRun.test_cases || []
      const assignedCount = testCasesInRun.filter(tc => 
        (tc.assigned_to === currentUserId.value || tc.assigneeId === currentUserId.value)
      ).length
      return h('div', { class: 'text-sm text-gray-600 text-center' }, `${assignedCount} of ${testCasesInRun.length}`)
    }
  },
  {
    id: 'project',
    accessorKey: 'project_id',
    header: 'Project',
    size: 200,
    cellFormatter: (props) => {
      const testRun = props.row.original
      const project = projects.value.find(p => p.id === testRun.project_id)
      return h('div', { class: 'text-sm text-gray-600' }, project?.name || project?.title || 'N/A')
    }
  },
  {
    id: 'created_at',
    accessorKey: 'created_at',
    header: 'Created',
    size: 150,
    cellFormatter: (props) => {
      const testRun = props.row.original
      return h('div', { class: 'text-sm text-gray-600' }, formatDate(testRun.created_at))
    }
  }
])

const accessibilityColumns = computed(() => [
  {
    id: 'issue_id',
    accessorKey: 'issue_id',
    header: 'Issue ID',
    size: 150,
    cellFormatter: (props) => {
      const issue = props.row.original
      return h('div', { 
        class: 'font-medium text-purple-600 cursor-pointer hover:underline',
        onClick: (e) => {
          e.stopPropagation()
          goToAccessibilityIssue(issue)
        }
      }, issue.issue_id || issue.id)
    }
  },
  {
    id: 'description',
    accessorKey: 'description',
    header: 'Description',
    size: 300,
    cellFormatter: (props) => {
      const issue = props.row.original
      return h('div', { class: 'font-medium' }, issue.description || 'Untitled')
    }
  },
  {
    id: 'page_url',
    accessorKey: 'page_url',
    header: 'Page URL',
    size: 250,
    cellFormatter: (props) => {
      const issue = props.row.original
      return h('div', { class: 'text-sm text-gray-600' }, issue.page_url || 'N/A')
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cellFormatter: (props) => {
      const issue = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          color: getHLTagColor(getAccessibilityStatusColor(issue.status)),
          size: 'sm',
          round: true
        }, { default: () => ACCESSIBILITY_STATUS_LABELS[issue.status] || issue.status || 'N/A' })
      ])
    }
  },
  {
    id: 'impact',
    accessorKey: 'impact',
    header: 'Impact',
    size: 120,
    cellFormatter: (props) => {
      const issue = props.row.original
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, {
          color: getHLTagColor(getAccessibilityImpactColor(issue.impact)),
          size: 'sm',
          round: true
        }, { default: () => ACCESSIBILITY_IMPACT_LABELS[issue.impact] || issue.impact || 'N/A' })
      ])
    }
  },
  {
    id: 'test_name',
    accessorKey: 'test_name',
    header: 'Test Name',
    size: 200,
    cellFormatter: (props) => {
      const issue = props.row.original
      return h('div', { class: 'text-sm text-gray-600' }, issue.test_name || 'N/A')
    }
  }
])

// Clear filters
const clearFeatureFilters = () => {
  featureFilters.value = {
    search: '',
    project: [],
    status: [],
    priority: [],
    assignmentType: []
  }
}

const clearBugFilters = () => {
  bugFilters.value = {
    search: '',
    project: [],
    status: [],
    priority: [],
    severity: []
  }
}

const clearTestRunFilters = () => {
  testRunFilters.value = {
    search: '',
    project: [],
    status: []
  }
}

const clearAccessibilityFilters = () => {
  accessibilityFilters.value = {
    search: '',
    status: [],
    impact: []
  }
}

// Load data
const loadFeatures = async () => {
  if (!currentUserId.value) return
  loadingFeatures.value = true
  try {
    const allFeatures = await featureService.getAll()
    features.value = allFeatures || []
  } catch (error) {
    console.error('Error loading features:', error)
    features.value = []
  } finally {
    loadingFeatures.value = false
  }
}

const loadBugs = async () => {
  if (!currentUserId.value) return
  loadingBugs.value = true
  try {
    const allBugs = await bugService.getAll()
    bugs.value = allBugs || []
  } catch (error) {
    console.error('Error loading bugs:', error)
    bugs.value = []
  } finally {
    loadingBugs.value = false
  }
}

const loadTestRuns = async () => {
  if (!currentUserId.value) return
  loadingTestRuns.value = true
  try {
    const allTestRuns = await testRunService.getAll()
    testRuns.value = allTestRuns || []
  } catch (error) {
    console.error('Error loading test runs:', error)
    testRuns.value = []
  } finally {
    loadingTestRuns.value = false
  }
}

const loadProjects = async () => {
  loadingProjects.value = true
  try {
    const allProjects = await projectService.getAll()
    projects.value = allProjects || []
  } catch (error) {
    console.error('Error loading projects:', error)
    projects.value = []
  } finally {
    loadingProjects.value = false
  }
}

const loadAccessibilityIssues = async () => {
  if (!currentUserId.value) return
  loadingAccessibilityIssues.value = true
  try {
    const result = await accessibilityService.queryIssues({
      assigned_to: [currentUserId.value],
      limit: 10000 // Get all assigned issues
    })
    accessibilityIssues.value = result.data || []
  } catch (error) {
    console.error('Error loading accessibility issues:', error)
    accessibilityIssues.value = []
  } finally {
    loadingAccessibilityIssues.value = false
  }
}

// Navigation
const goToFeature = (feature) => {
  const featureId = typeof feature === 'string' ? feature : feature.id
  const projectId = typeof feature === 'object' ? feature.project_id : null
  if (projectId) {
    router.push({
      path: `/projects/${projectId}/features/${featureId}`,
      query: { from: 'assigned-to-me' }
    })
  } else {
    const featureData = features.value.find(f => f.id === featureId)
    if (featureData?.project_id) {
      router.push({
        path: `/projects/${featureData.project_id}/features/${featureId}`,
        query: { from: 'assigned-to-me' }
      })
    }
  }
}

const goToBug = (bug) => {
  const bugId = typeof bug === 'string' ? bug : bug.id
  router.push({
    path: `/bugs/${bugId}`,
    query: { from: 'assigned-to-me' }
  })
}

const goToTestRun = (testRun) => {
  const testRunId = typeof testRun === 'string' ? testRun : testRun.id
  router.push({
    path: `/test-runs/${testRunId}`,
    query: { from: 'assigned-to-me' }
  })
}

const goToAccessibilityIssue = (issue) => {
  // Navigate to accessibility reports page with filters for this issue
  router.push({
    path: '/reports',
    query: { 
      tab: 'accessibility',
      view: 'detailed',
      from: 'assigned-to-me',
      issueId: issue.id || issue.issue_id
    }
  })
}

// Load data on mount
onMounted(async () => {
  await Promise.all([
    loadFeatures(),
    loadBugs(),
    loadTestRuns(),
    loadProjects(),
    loadAccessibilityIssues()
  ])
})
</script>

